import { useEffect, useRef } from 'react'
import * as THREE from 'three'

const HeroBackground = () => {
  const mountRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<{
    scene: THREE.Scene
    camera: THREE.OrthographicCamera
    renderer: THREE.WebGLRenderer
    lines: THREE.Line[]
    points: THREE.Points[]
    animationId: number
  } | null>(null)

  useEffect(() => {
    if (!mountRef.current) return

    const width = mountRef.current.clientWidth
    const height = mountRef.current.clientHeight

    // Scene setup
    const scene = new THREE.Scene()
    const camera = new THREE.OrthographicCamera(-width / 2, width / 2, height / 2, -height / 2, 1, 1000)
    camera.position.z = 500

    const renderer = new THREE.WebGLRenderer({ 
      alpha: true, 
      antialias: true 
    })
    renderer.setSize(width, height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    mountRef.current.appendChild(renderer.domElement)

    // Create network pattern
    const lines: THREE.Line[] = []
    const points: THREE.Points[] = []
    const nodeCount = 30
    const nodes: Array<{ x: number; y: number; z: number }> = []

    // Create nodes
    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: (Math.random() - 0.5) * width * 1.5,
        y: (Math.random() - 0.5) * height * 1.5,
        z: (Math.random() - 0.5) * 200
      })
    }

    // Create connections
    const lineGeometry = new THREE.BufferGeometry()
    const linePositions: number[] = []
    const lineColors: number[] = []

    nodes.forEach((node, i) => {
      nodes.slice(i + 1).forEach(otherNode => {
        const distance = Math.sqrt(
          Math.pow(node.x - otherNode.x, 2) +
          Math.pow(node.y - otherNode.y, 2) +
          Math.pow(node.z - otherNode.z, 2)
        )
        
        if (distance < 400) {
          linePositions.push(node.x, node.y, node.z)
          linePositions.push(otherNode.x, otherNode.y, otherNode.z)
          
          const opacity = 1 - distance / 400
          lineColors.push(1.0, 0.42, 0.21, opacity) // Orange
          lineColors.push(0.29, 0.87, 0.50, opacity * 0.5) // Green
        }
      })
    })

    lineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3))
    lineGeometry.setAttribute('color', new THREE.Float32BufferAttribute(lineColors, 4))

    const lineMaterial = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.3,
      linewidth: 1
    })

    const line = new THREE.LineSegments(lineGeometry, lineMaterial)
    scene.add(line)
    lines.push(line)

    // Create glowing points
    const pointGeometry = new THREE.BufferGeometry()
    const pointPositions: number[] = []
    const pointSizes: number[] = []

    nodes.forEach(node => {
      pointPositions.push(node.x, node.y, node.z)
      pointSizes.push(Math.random() * 3 + 2)
    })

    pointGeometry.setAttribute('position', new THREE.Float32BufferAttribute(pointPositions, 3))
    pointGeometry.setAttribute('size', new THREE.Float32BufferAttribute(pointSizes, 1))

    const pointMaterial = new THREE.PointsMaterial({
      size: 4,
      color: 0xff6b35,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending
    })

    const pointCloud = new THREE.Points(pointGeometry, pointMaterial)
    scene.add(pointCloud)
    points.push(pointCloud)

    // Animation
    let animationId: number = 0
    const animate = () => {
      animationId = requestAnimationFrame(animate)

      // Subtle rotation
      line.rotation.z += 0.0002
      pointCloud.rotation.z += 0.0002

      // Pulse effect
      const time = Date.now() * 0.001
      pointCloud.material.opacity = 0.4 + Math.sin(time) * 0.2

      renderer.render(scene, camera)
    }
    // Start animation loop
    animationId = requestAnimationFrame(animate)

    sceneRef.current = { scene, camera, renderer, lines, points, animationId: animationId || 0 }

    // Handle resize
    const handleResize = () => {
      if (!mountRef.current) return
      const newWidth = mountRef.current.clientWidth
      const newHeight = mountRef.current.clientHeight

      camera.left = -newWidth / 2
      camera.right = newWidth / 2
      camera.top = newHeight / 2
      camera.bottom = -newHeight / 2
      camera.updateProjectionMatrix()
      renderer.setSize(newWidth, newHeight)
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      if (animationId) {
        cancelAnimationFrame(animationId)
      }
      if (sceneRef.current) {
        renderer.dispose()
        lineGeometry.dispose()
        lineMaterial.dispose()
        pointGeometry.dispose()
        pointMaterial.dispose()
        if (mountRef.current && mountRef.current.contains(renderer.domElement)) {
          mountRef.current.removeChild(renderer.domElement)
        }
      }
    }
  }, [])

  return <div ref={mountRef} className="hero-background-three" />
}

export default HeroBackground
